import { GemsApi_StoneShape, type StoneShapeFragment } from '@/graphql/api'
import { computed, type ComputedRef, toValue } from 'vue'
import type { RefOrGetter } from '@/helpers'
import type { RingBuilderCentreStoneShapeConstraint } from '@/domain/product/ring/ring-builder.ts'
import {
    useRingBuilder,
    useRingBuilderRingShape,
} from '@/plugins/ring-builder.ts'

const ordered: GemsApi_StoneShape[] = [
    GemsApi_StoneShape.Round,
    GemsApi_StoneShape.Cushion,
    GemsApi_StoneShape.Oval,
    GemsApi_StoneShape.Emerald,
    GemsApi_StoneShape.Pear,
    GemsApi_StoneShape.Hexagonal,
    GemsApi_StoneShape.Princess,
    GemsApi_StoneShape.Asscher,
    GemsApi_StoneShape.Marquise,
    GemsApi_StoneShape.Radiant,
    GemsApi_StoneShape.Heart,
    GemsApi_StoneShape.European,
] as const

export function findShapeOrder(code: GemsApi_StoneShape): number {
    const order = ordered.findIndex((c) => c === code)

    if (-1 === order) {
        return ordered.length
    }

    return order
}

function sortShapes(shapes: StoneShapeFragment[]): StoneShapeFragment[] {
    return shapes.sort((a: StoneShapeFragment, b: StoneShapeFragment) => {
        const orderA = findShapeOrder(a.systemCode)
        const orderB = findShapeOrder(b.systemCode)

        return orderA - orderB
    })
}

export function useOrderedShapeFragments(
    shapes: RefOrGetter<StoneShapeFragment[] | undefined>
): ComputedRef<StoneShapeFragment[] | undefined> {
    return computed(() => {
        const value = toValue(shapes)

        if (!value) {
            return undefined
        }

        return sortShapes(value)
    })
}

export function useRingShapeConstraintForNonSKU(
    shapes: RefOrGetter<StoneShapeFragment[] | undefined>
): {
    ringShape: ComputedRef<RingBuilderCentreStoneShapeConstraint | undefined>
    shapes: ComputedRef<StoneShapeFragment[] | undefined>
} {
    const ringShape = useRingBuilderRingShape()

    const { mountFragment } = useRingBuilder()
    const ringBuilderOtherShapes = computed(() =>
        mountFragment.value?.ring?.variationsByCentreStoneShape.map(
            (variation) => variation.centreStoneConstraints.shape.systemCode
        )
    )

    const shapesComputed = computed((): StoneShapeFragment[] | undefined => {
        const value = toValue(shapes)

        if (!value) {
            return undefined
        }

        const filteredShapes = ringBuilderOtherShapes.value
            ? value.filter((item) =>
                  ringBuilderOtherShapes.value?.includes(item.systemCode)
              )
            : value

        return sortShapes(filteredShapes)
    })

    return {
        shapes: shapesComputed,
        ringShape,
    }
}
